import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Text,
  Title,
  Group,
  SimpleGrid,
  ThemeIcon,
  RingProgress,
  Table,
  Badge,
  Loader,
  Center,
  ScrollArea,
  Card,
  Alert,
  Button // Tambahkan Button
} from "@mantine/core";
import {
  IconTruckDelivery,
  IconSchool,
  IconChecklist,
  IconChartPie,
  IconAlertCircle,
  IconRefresh
} from "@tabler/icons-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from "../../services/api";

export default function AdminDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/admin/dashboard");
      setData(res.data);
    } catch (err) {
      console.error("Gagal memuat dashboard:", err);
      // Ambil pesan error spesifik dari backend jika ada
      const msg = err.response?.data?.error || "Gagal mengambil data dashboard.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) return <Center h="100vh"><Loader size="xl" /></Center>;

  if (error) {
    return (
      // FIX FLEX DIRECTION: Gunakan style prop
      <Center h="80vh" style={{ flexDirection: 'column' }}>
         <Alert icon={<IconAlertCircle size={16} />} title="Error Backend" color="red" mb="md">
            {error}
         </Alert>
         <Button variant="subtle" onClick={fetchDashboard} leftSection={<IconRefresh size={16}/>}>
            Coba Refresh
         </Button>
      </Center>
    );
  }

  // --- SAFE DESTRUCTURING (ANTI CRASH) ---
  // Kita pastikan data tidak null sebelum destructuring
  const safeData = data || {}; 
  
  const { 
    stats = { 
        total_distribusi: 0, 
        total_sekolah: 0, 
        distribusi_selesai: 0, 
        avg_nutrisi: { avg_protein: 0 } 
    }, 
    chart = [], 
    recent = [] 
  } = safeData;

  // --- KOMPONEN ROWS TABEL ---
  const rows = recent.map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td>
        <Text fz="sm" fw={500}>{new Date(item.tanggal).toLocaleDateString('id-ID')}</Text>
      </Table.Td>
      <Table.Td>
        <Text fz="sm">{item.sekolah}</Text>
        <Text fz="xs" c="dimmed">{item.menu}</Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
            <Badge size="sm" variant={item.has_penerimaan ? "filled" : "outline"} color={item.has_penerimaan ? "green" : "gray"}>Terima</Badge>
            <Badge size="sm" variant={item.has_nutrisi ? "filled" : "outline"} color={item.has_nutrisi ? "blue" : "red"}>Gizi</Badge>
            <Badge size="sm" variant={item.has_monitoring ? "filled" : "outline"} color={item.has_monitoring ? "violet" : "orange"}>Monitor</Badge>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  const StatCard = ({ title, value, icon, color, desc }) => (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between">
        <div>
          <Text size="xs" c="dimmed" fw={700} tt="uppercase">{title}</Text>
          <Text fw={700} size="xl">{value}</Text>
          {desc && <Text size="xs" c="dimmed" mt="xs">{desc}</Text>}
        </div>
        <ThemeIcon variant="light" color={color} size="xl" radius="md">{icon}</ThemeIcon>
      </Group>
    </Paper>
  );

  return (
    <div style={{ padding: 20 }}>
      <Group justify="space-between" mb="lg">
         <div>
            <Title order={2}>Dashboard Administrator</Title>
            <Text c="dimmed">Gambaran umum operasional distribusi GiziMon</Text>
         </div>
         <Button variant="light" onClick={fetchDashboard} leftSection={<IconRefresh size={16}/>}>Refresh</Button>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} mb="lg">
        <StatCard title="Total Distribusi" value={stats.total_distribusi} icon={<IconTruckDelivery size={28}/>} color="blue" desc="Kegiatan" />
        <StatCard title="Sekolah Terdaftar" value={stats.total_sekolah} icon={<IconSchool size={28}/>} color="cyan" desc="Mitra" />
        <StatCard title="Selesai" value={stats.distribusi_selesai} icon={<IconChecklist size={28}/>} color="teal" desc="Lengkap" />
        
        <Paper withBorder p="md" radius="md">
            <Group>
                <RingProgress size={80} roundCaps thickness={8} sections={[{ value: 75, color: 'orange' }]} label={<Center><IconChartPie size={20} /></Center>} />
                <div>
                    <Text c="dimmed" size="xs" tt="uppercase" fw={700}>Rata-rata Protein</Text>
                    <Text fw={700} size="xl">
                        {stats.avg_nutrisi?.avg_protein ? Number(stats.avg_nutrisi.avg_protein).toFixed(1) : "0"}g
                    </Text>
                    <Text size="xs" c="dimmed">Per porsi</Text>
                </div>
            </Group>
        </Paper>
      </SimpleGrid>

      <Grid>
        <Grid.Col span={{ base: 12, md: 8 }}>
            <Card withBorder shadow="sm" radius="md">
                <Title order={4} mb="md">Status Terkini</Title>
                <ScrollArea>
                    <Table verticalSpacing="sm">
                        <Table.Thead bg="gray.0"><Table.Tr><Table.Th>Tanggal</Table.Th><Table.Th>Sekolah</Table.Th><Table.Th>Status</Table.Th></Table.Tr></Table.Thead>
                        <Table.Tbody>{rows.length > 0 ? rows : <Table.Tr><Table.Td colSpan={3} align="center">Belum ada data</Table.Td></Table.Tr>}</Table.Tbody>
                    </Table>
                </ScrollArea>
            </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
            <Card withBorder shadow="sm" radius="md" h="100%">
                <Title order={4} mb="md">Grafik Mingguan</Title>
                <div style={{ height: 250, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chart}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" tickFormatter={(v) => new Date(v).getDate()} />
                            <YAxis allowDecimals={false} />
                            <RechartsTooltip />
                            <Bar dataKey="count" fill="#228be6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </Card>
        </Grid.Col>
      </Grid>
    </div>
  );
}